import type { Addon, CustomService, Faq, GalleryItem, Package, SiteSettings, Testimonial } from './types';

// Built-in content. The public site falls back to this when Firestore is
// empty or not yet configured, and the admin panel can seed Firestore with
// it in one click ("Load starter content"). Everything here is editable
// from the admin panel afterwards.

export const defaultPackages: Package[] = [
  {
    id: 'express-wash',
    name: 'Express Detail',
    description: 'A fast but thorough refresh — perfect for maintaining a vehicle that is detailed regularly.',
    category: 'full',
    features: [
      'Hand wash & dry with premium mitts',
      'Wheel faces & tires cleaned and dressed',
      'Interior vacuum (seats, carpets, mats)',
      'Interior wipe-down of all surfaces',
      'Windows in & out',
      'Spray sealant (2–3 months protection)',
    ],
    prices: { car: 89, midsize: 109, large: 129, oversized: 149 },
    durationHours: 1.5,
    popular: false,
    active: true,
    order: 1,
  },
  {
    id: 'interior-revival',
    name: 'Interior Revival',
    description: 'Deep interior restoration — steam, extraction, and conditioning for a factory-fresh cabin.',
    category: 'interior',
    features: [
      'Full interior vacuum incl. trunk',
      'Steam cleaning of vents, seams & cupholders',
      'Shampoo & hot-water extraction of carpets and cloth seats',
      'Leather cleaned & conditioned',
      'All plastics and trim deep cleaned & protected',
      'Odor treatment',
      'Streak-free windows',
    ],
    prices: { car: 179, midsize: 209, large: 239, oversized: 279 },
    durationHours: 3.5,
    popular: false,
    active: true,
    order: 2,
  },
  {
    id: 'exterior-enhance',
    name: 'Exterior Enhancement',
    description: 'Decontamination, machine gloss enhancement, and durable sealant protection for serious shine.',
    category: 'exterior',
    features: [
      'Foam pre-wash & hand wash',
      'Iron & tar chemical decontamination',
      'Full clay bar treatment',
      'One-step machine polish (gloss boost, light swirl removal)',
      '6-month paint sealant',
      'Wheels, tires & trim detailed and dressed',
      'Exterior glass polished',
    ],
    prices: { car: 249, midsize: 289, large: 329, oversized: 379 },
    durationHours: 4,
    popular: false,
    active: true,
    order: 3,
  },
  {
    id: 'apex-signature',
    name: 'Apex Signature Detail',
    description: 'Our flagship inside-and-out transformation. The full Apex treatment, bumper to bumper.',
    category: 'full',
    features: [
      'Everything in Interior Revival',
      'Everything in Exterior Enhancement',
      'Engine bay cleaning & dressing',
      'Door jambs & fuel door detailed',
      'Headlight inspection & polish',
      'Free 2-week follow-up spot check',
    ],
    prices: { car: 389, midsize: 449, large: 509, oversized: 579 },
    durationHours: 7,
    popular: true,
    active: true,
    order: 4,
  },
  {
    id: 'ceramic-shield',
    name: 'Ceramic Coating',
    description: 'Multi-year professional ceramic protection with full paint prep and correction pass.',
    category: 'exterior',
    features: [
      'Full decontamination wash & clay',
      'Single-stage paint correction included',
      'Panel wipe & surface prep',
      'Professional-grade ceramic coating (3+ year)',
      'Coated wheels faces & exterior glass',
      'Aftercare kit & maintenance guidance',
    ],
    prices: { car: 799, midsize: 899, large: 999, oversized: 1149 },
    durationHours: 10,
    popular: false,
    active: true,
    order: 5,
  },
];

export const defaultAddons: Addon[] = [
  { id: 'pet-hair', name: 'Pet Hair Removal', description: 'Heavy pet hair removal from carpets and seats.', price: 40, fromPrice: true, active: true, order: 1 },
  { id: 'engine-bay', name: 'Engine Bay Detail', description: 'Degrease, rinse and dress the engine bay.', price: 50, fromPrice: false, active: true, order: 2 },
  { id: 'headlight-restore', name: 'Headlight Restoration', description: 'Sand and polish hazy headlights back to clarity.', price: 70, fromPrice: false, active: true, order: 3 },
  { id: 'odor-bomb', name: 'Ozone Odor Treatment', description: 'Eliminate smoke, food or pet odors at the source.', price: 60, fromPrice: false, active: true, order: 4 },
  { id: 'glass-coat', name: 'Windshield Ceramic Coat', description: 'Rain-repellent ceramic coating for the windshield.', price: 45, fromPrice: false, active: true, order: 5 },
  { id: 'seat-extraction', name: 'Extra Stain Extraction', description: 'Additional hot-water extraction for tough stains.', price: 35, fromPrice: true, active: true, order: 6 },
];

export const defaultServices: CustomService[] = [
  {
    id: 'audio',
    name: 'Car Audio & Sound',
    description: 'From head unit upgrades to full custom systems with amps, subs, and sound deadening.',
    icon: 'speaker',
    features: ['Head units & CarPlay/Android Auto', 'Speaker & amplifier installs', 'Custom subwoofer enclosures', 'Sound deadening'],
    priceFrom: 150,
    comingSoon: false,
    active: true,
    order: 1,
  },
  {
    id: 'lighting',
    name: 'Custom Lighting',
    description: 'LED conversions, accent lighting, underglow, and halo installs done clean — no hack jobs.',
    icon: 'lightbulb',
    features: ['LED headlight conversions', 'Interior ambient lighting', 'Underglow & accent kits', 'Sequential turn signals'],
    priceFrom: 100,
    comingSoon: false,
    active: true,
    order: 2,
  },
  {
    id: 'wraps',
    name: 'Vinyl Wraps & Tint',
    description: 'Color-change wraps, chrome deletes, decals, and window tint with premium films.',
    icon: 'layers',
    features: ['Full & partial color-change wraps', 'Chrome delete packages', 'Window tint', 'Custom decals & accents'],
    priceFrom: 250,
    comingSoon: true,
    active: true,
    order: 3,
  },
  {
    id: 'ppf',
    name: 'Paint Protection Film',
    description: 'Self-healing film for high-impact zones — keep rock chips off your paint for good.',
    icon: 'shield',
    features: ['Partial & full front-end coverage', 'Self-healing top coat', 'Track packages', 'Headlight protection'],
    priceFrom: 400,
    comingSoon: true,
    active: true,
    order: 4,
  },
];

export const defaultFaqs: Faq[] = [
  { id: 'faq-1', question: 'How long does a detail take?', answer: 'An express detail takes about 90 minutes, while our Signature Detail runs most of a day. Every booking shows an estimated duration, and we confirm the exact timing with you when we confirm your appointment.', order: 1 },
  { id: 'faq-2', question: 'Do I need to do anything before my appointment?', answer: 'Just remove personal items and anything valuable from the vehicle. Child seats can stay — let us know in the booking notes and we will clean around or under them as you prefer.', order: 2 },
  { id: 'faq-3', question: 'How do payments work?', answer: 'Booking online is free and no card is required — you pay when the work is done, by card, cash, or tap-to-pay. For ceramic coatings and custom work we may take a small deposit, which we confirm with you first.', order: 3 },
  { id: 'faq-4', question: 'What if it rains right after my detail?', answer: 'Rain itself will not undo the protection we apply — sealants and coatings need only a short cure window, which we time around the forecast. If weather forces a reschedule, we will contact you and find the next best slot.', order: 4 },
  { id: 'faq-5', question: 'How often should I get my car detailed?', answer: 'For most daily drivers we recommend a full detail every 4–6 months with express washes in between. Coated vehicles can stretch longer — we will build a maintenance plan that fits your car and budget.', order: 5 },
];

// Placeholder portfolio shots (Unsplash). Replace with your own work
// from the admin panel → Gallery.
export const defaultGallery: GalleryItem[] = [
  { id: 'g1', createdAt: 1, url: 'https://images.unsplash.com/photo-1601362840469-51e4d8d58785?q=80&w=1200&auto=format&fit=crop', storagePath: '', caption: 'Gloss-boosted black paint after enhancement polish', category: 'exterior' },
  { id: 'g2', createdAt: 2, url: 'https://images.unsplash.com/photo-1607860108855-64acf2078ed9?q=80&w=1200&auto=format&fit=crop', storagePath: '', caption: 'Signature Detail — full exterior finish', category: 'exterior' },
  { id: 'g3', createdAt: 3, url: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?q=80&w=1200&auto=format&fit=crop', storagePath: '', caption: 'Deep red after ceramic coating', category: 'ceramic coating' },
  { id: 'g4', createdAt: 4, url: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=80&w=1200&auto=format&fit=crop', storagePath: '', caption: 'Paint correction — mirror finish', category: 'paint correction' },
  { id: 'g5', createdAt: 5, url: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?q=80&w=1200&auto=format&fit=crop', storagePath: '', caption: 'Show-ready stance', category: 'exterior' },
  { id: 'g6', createdAt: 6, url: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?q=80&w=1200&auto=format&fit=crop', storagePath: '', caption: 'Fresh full detail, ready for delivery', category: 'exterior' },
];

export const defaultTestimonials: Testimonial[] = [
  { id: 't1', createdAt: 1, name: 'Jordan M.', vehicle: '2021 F-150', rating: 5, text: 'My truck looked better than the day I bought it. The interior extraction pulled out stains I thought were permanent. Worth every penny.', approved: true, featured: true },
  { id: 't2', createdAt: 2, name: 'Alyssa R.', vehicle: '2019 Civic Si', rating: 5, text: 'Booked the Signature Detail before selling my car — it added real value at trade-in. Communication was great from booking to pickup.', approved: true, featured: true },
  { id: 't3', createdAt: 3, name: 'Derek T.', vehicle: '2022 Tesla Model 3', rating: 5, text: 'The ceramic coating is unreal. Water flies off the hood and washing takes half the time. Meticulous work.', approved: true, featured: true },
];

export const defaultSettings: SiteSettings = {
  businessName: 'Apex Car Care',
  tagline: 'Premium Auto Detailing & Custom Work',
  heroTitle: 'Your Car, At Its Absolute Peak.',
  heroSubtitle:
    'Premium detailing, paint correction, and ceramic protection — plus custom audio, lighting, and styling. One shop that treats your vehicle like its own.',
  heroImage:
    'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?q=80&w=1800&auto=format&fit=crop',
  phone: '(613) 555-0199',
  email: 'bookings@apexcarcare.net',
  city: 'Ottawa, ON',
  serviceArea: 'serving Ottawa & surrounding areas',
  aboutText:
    'Apex Car Care was built on a simple idea: most "details" are just expensive car washes. We do it differently — measured paint, documented processes, and products we trust on our own vehicles. Detailing is where we start; custom audio, lighting, and styling is where we are headed. One shop, one standard.',
  announcementEnabled: false,
  announcementText: 'Grand opening special — 15% off all packages this month!',
  // Mobile service is limited to the regions below — a center postal code
  // plus a radius (km). Postal codes outside every region are blocked.
  // Adjust the center, radius, fee, or add more regions in Admin → Site
  // Settings → Mobile Service & Travel Fees.
  travel: {
    mode: 'regions',
    flatFee: 45,
    regions: [
      { id: 'region-ottawa', name: 'Ottawa', centerPostal: 'K1P', radiusKm: 35, fee: 45 },
    ],
    allowOutsideRequests: true,
    outsideMessage:
      "That postal code is outside our current service area — send the request anyway and we'll reach out if we can make the trip work.",
  },
  allowDropoff: true,
  shopAddress: '',
  socials: { instagram: '', facebook: '', tiktok: '', youtube: '', google: '' },
  hours: {
    mon: { open: '08:00', close: '18:00', closed: false },
    tue: { open: '08:00', close: '18:00', closed: false },
    wed: { open: '08:00', close: '18:00', closed: false },
    thu: { open: '08:00', close: '18:00', closed: false },
    fri: { open: '08:00', close: '18:00', closed: false },
    sat: { open: '09:00', close: '16:00', closed: false },
    sun: { open: '09:00', close: '16:00', closed: true },
  },
  booking: {
    slotStart: 8,
    slotEnd: 15,
    slotIntervalHours: 1,
    daysAhead: 30,
    minLeadHours: 12,
  },
  notifications: {
    vapidKey: '',
  },
};
